import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, ChevronRight, Download, Network, Plus, RefreshCw, Save } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  CommunicationLink,
  ComplianceTopology,
  projectsApi,
  SecurityZoneCode,
  TopologyNode,
  type ProjectComplianceProfile,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { useTranslation } from 'react-i18next'

const ZONES: Array<{ code: SecurityZoneCode; key: string }> = [
  { code: 'machinery_cargo', key: 'machineryCargo' },
  { code: 'navigation_communication', key: 'navigationCommunication' },
  { code: 'safety', key: 'safety' },
  { code: 'data_exchange_monitoring', key: 'dataExchangeMonitoring' },
]

interface Props {
  complianceProfiles?: ProjectComplianceProfile[]
  projectId: string
  canEdit: boolean
  onBack: () => void
  onOpenCsdd: () => void
}

const EMPTY: ComplianceTopology = { nodes: [], links: [], conduits: [], controls: [], issues: [] }

export const ZoneConduitWorkspace: React.FC<Props> = ({ projectId, canEdit, onBack, onOpenCsdd, complianceProfiles = [] }) => {
  const { t } = useTranslation()
  const [selectedProfileCode, setSelectedProfileCode] = useState('')
  const profileCode = complianceProfiles.find(profile => profile.profile_code === selectedProfileCode)?.profile_code
    ?? complianceProfiles[0]?.profile_code
  const { success, error: showError } = useToast()
  const [data, setData] = useState<ComplianceTopology>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [building, setBuilding] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [newLink, setNewLink] = useState({
    from_node_id: '', to_node_id: '', direction: 'unknown',
    physical_standard: 'unknown', protocol: '', purpose: '',
  })

  const buildDraft = useCallback(async (force = false) => {
    setBuilding(true)
    try {
      const next = await projectsApi.buildComplianceTopology(projectId, force)
      setData(next)
      success(t('zones.draftReady'))
    } catch (error: any) {
      showError(error?.message ?? t('zones.loadFailed'))
    } finally {
      setBuilding(false)
    }
  }, [projectId, showError, success, t])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const next = await projectsApi.getComplianceTopology(projectId)
      setData(next)
      if (!next.nodes.length && canEdit) await buildDraft(false)
    } catch (error: any) {
      showError(error?.message ?? t('zones.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [buildDraft, canEdit, projectId, showError, t])

  useEffect(() => { void load() }, [load])

  const nodesById = useMemo(() => new Map(data.nodes.map(node => [node.id, node])), [data.nodes])
  const externalNodes = data.nodes.filter(node => node.node_type === 'untrusted')

  const updateNode = async (node: TopologyNode, patch: Partial<TopologyNode>) => {
    setSaving(node.id)
    try {
      setData(await projectsApi.updateTopologyNode(projectId, node.id, patch))
    } catch (error: any) {
      showError(error?.message ?? t('common.saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  const updateLink = async (link: CommunicationLink, patch: Partial<CommunicationLink>) => {
    setSaving(link.id)
    try {
      setData(await projectsApi.updateCommunicationLink(projectId, link.id, patch))
    } catch (error: any) {
      showError(error?.message ?? t('common.saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  const addLink = async () => {
    if (!newLink.from_node_id || !newLink.to_node_id) return
    setSaving('new-link')
    try {
      setData(await projectsApi.createCommunicationLink(projectId, {
        ...newLink,
        direction: newLink.direction as CommunicationLink['direction'],
        physical_standard: newLink.physical_standard as CommunicationLink['physical_standard'],
      }))
      setNewLink({ from_node_id: '', to_node_id: '', direction: 'unknown', physical_standard: 'unknown', protocol: '', purpose: '' })
    } catch (error: any) {
      showError(error?.message ?? t('common.saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  if (loading && !data.nodes.length) {
    return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><RefreshCw className="h-4 w-4 animate-spin" />{t('common.loading')}</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
          <ArrowLeft className="h-4 w-4" /> {t('projectDetail.backToCompliance')}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {complianceProfiles.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-500">
              {t('raGeneration.classProfile')}
              <select aria-label={t('raGeneration.classProfile')} value={profileCode} disabled={downloading} onChange={event => setSelectedProfileCode(event.target.value)} className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900">
                {complianceProfiles.map(profile => <option key={profile.profile_code} value={profile.profile_code}>{profile.class_society} · {profile.edition_label}</option>)}
              </select>
            </label>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" disabled={building} onClick={() => buildDraft(true)}>
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${building ? 'animate-spin' : ''}`} />
              {t('zones.rebuild')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!data.nodes.length || downloading}
            onClick={async () => {
              setDownloading(true)
              try {
                await projectsApi.downloadComplianceDocument(projectId, 'zcd', `Zone_and_Conduit_Diagram${profileCode ? `_${profileCode}` : ''}.docx`, '0', profileCode)
                success(t('zones.downloaded'))
              } catch (error: any) {
                showError(error?.message ?? t('zones.generateFailed'))
              } finally {
                setDownloading(false)
              }
            }}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" /> {t('zones.generateDocx')}
          </Button>
          <Button size="sm" onClick={onOpenCsdd}>{t('zones.continueCsdd')}</Button>
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
              <Network className="h-5 w-5 text-blue-500" /> {t('zones.title')}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('zones.description')}</p>
          </div>
          <span className={`rounded px-2 py-1 text-xs font-medium ${data.issues.length ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {data.issues.length ? t('zones.issueCount', { count: data.issues.length }) : t('zones.ready')}
          </span>
        </div>
      </section>

      {data.issues.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" /> {t('zones.exceptions')}
          </h3>
          <ul className="mt-2 grid gap-1 text-xs text-amber-800 dark:text-amber-200 sm:grid-cols-2">
            {data.issues.slice(0, 12).map((issue, index) => <li key={`${issue.kind}-${issue.id}-${index}`}>• {issue.message}</li>)}
          </ul>
          {data.issues.length > 12 && <p className="mt-1 text-xs text-amber-700">+{data.issues.length - 12}</p>}
        </section>
      )}

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {ZONES.map(zone => {
          const zoneNodes = data.nodes.filter(node => node.zone_code === zone.code)
          return (
            <details key={zone.code} className="group rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                <span className="flex min-w-0 items-center gap-1">
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" />
                  <span className="truncate">{t(`zones.names.${zone.key}`)}</span>
                </span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500 dark:bg-slate-700 dark:text-slate-300">{zoneNodes.length}</span>
              </summary>
              <div className="mt-2 max-h-72 space-y-1 overflow-y-auto pr-1">
                {zoneNodes.map(node => (
                  <div key={node.id} className="flex items-center gap-2 rounded border border-slate-100 px-2 py-1.5 text-xs dark:border-slate-700">
                    <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200" title={node.system_name}>{node.system_name}</span>
                    {canEdit && (
                      <select
                        aria-label={t('zones.zone')}
                        value={node.zone_code ?? ''}
                        onChange={event => void updateNode(node, { zone_code: event.target.value as SecurityZoneCode })}
                        disabled={saving === node.id}
                        className="w-24 rounded border border-slate-200 bg-white px-1 py-0.5 text-[9px] dark:border-slate-600 dark:bg-slate-900"
                      >
                        {ZONES.map(option => <option key={option.code} value={option.code}>{t(`zones.names.${option.key}`)}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )
        })}
      </div>

      {externalNodes.length > 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-300">
          {t('zones.external')}: {externalNodes.map(node => node.system_name).join(', ')}
        </div>
      )}

      <details className="group rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-slate-800 dark:text-slate-100">
          <span className="flex items-center gap-1.5"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />{t('zones.communicationLinks')}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500 dark:bg-slate-700 dark:text-slate-300">{data.links.length}</span>
        </summary>
        <div className="mt-3 max-h-[55vh] space-y-2 overflow-y-auto pr-1">
          {data.links.map(link => {
            const from = nodesById.get(link.from_node_id)
            const to = nodesById.get(link.to_node_id)
            return (
              <div key={link.id} className="grid gap-2 rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-700 md:grid-cols-[minmax(180px,1fr)_110px_120px_minmax(140px,1fr)]">
                <div className="font-medium text-slate-700 dark:text-slate-200">{from?.system_name ?? '?'} → {to?.system_name ?? '?'}</div>
                <select value={link.direction} disabled={!canEdit || saving === link.id} onChange={event => void updateLink(link, { direction: event.target.value as CommunicationLink['direction'] })} className="rounded border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900">
                  <option value="unknown">{t('zones.unknown')}</option>
                  <option value="unidirectional">{t('zones.oneWay')}</option>
                  <option value="bidirectional">{t('zones.twoWay')}</option>
                </select>
                <select value={link.physical_standard} disabled={!canEdit || saving === link.id} onChange={event => void updateLink(link, { physical_standard: event.target.value as CommunicationLink['physical_standard'] })} className="rounded border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-900">
                  {['unknown', 'ethernet', 'rs232', 'rs422', 'rs485', 'hardwired', 'wireless'].map(value => <option key={value} value={value}>{value.toUpperCase()}</option>)}
                </select>
                <input value={link.protocol ?? ''} readOnly={!canEdit} onBlur={event => { if (event.target.value !== (link.protocol ?? '')) void updateLink(link, { protocol: event.target.value }) }} placeholder={t('zones.protocol')} className="rounded border border-slate-200 px-2 py-1 dark:border-slate-600 dark:bg-slate-900" />
              </div>
            )
          })}
          {!data.links.length && <p className="text-xs text-slate-400">{t('zones.noLinks')}</p>}
        </div>

        {canEdit && data.nodes.length > 1 && (
          <div className="mt-4 grid gap-2 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40 md:grid-cols-2 xl:grid-cols-6">
            <select value={newLink.from_node_id} onChange={event => setNewLink(value => ({ ...value, from_node_id: event.target.value }))} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900">
              <option value="">{t('zones.from')}</option>
              {data.nodes.map(node => <option key={node.id} value={node.id}>{node.system_name}</option>)}
            </select>
            <select value={newLink.to_node_id} onChange={event => setNewLink(value => ({ ...value, to_node_id: event.target.value }))} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900">
              <option value="">{t('zones.to')}</option>
              {data.nodes.map(node => <option key={node.id} value={node.id}>{node.system_name}</option>)}
            </select>
            <select value={newLink.direction} onChange={event => setNewLink(value => ({ ...value, direction: event.target.value }))} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900">
              <option value="unknown">{t('zones.unknown')}</option><option value="unidirectional">{t('zones.oneWay')}</option><option value="bidirectional">{t('zones.twoWay')}</option>
            </select>
            <select value={newLink.physical_standard} onChange={event => setNewLink(value => ({ ...value, physical_standard: event.target.value }))} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900">
              {['unknown', 'ethernet', 'rs232', 'rs422', 'rs485', 'hardwired', 'wireless'].map(value => <option key={value} value={value}>{value.toUpperCase()}</option>)}
            </select>
            <input value={newLink.protocol} onChange={event => setNewLink(value => ({ ...value, protocol: event.target.value }))} placeholder={t('zones.protocol')} className="rounded border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900" />
            <Button size="sm" disabled={saving === 'new-link' || !newLink.from_node_id || !newLink.to_node_id} onClick={() => void addLink()}><Plus className="mr-1 h-3.5 w-3.5" />{t('common.add')}</Button>
          </div>
        )}
      </details>

      <details className="group rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-slate-800 dark:text-slate-100">
          <span className="flex items-center gap-1.5"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />{t('zones.conduits')}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500 dark:bg-slate-700 dark:text-slate-300">{data.conduits.length}</span>
        </summary>
        <div className="mt-3 grid max-h-[65vh] gap-3 overflow-y-auto pr-1 lg:grid-cols-2">
          {data.conduits.map(conduit => (
            <ConduitEditor
              key={conduit.id}
              conduit={conduit}
              canEdit={canEdit}
              saving={saving === conduit.id}
              onSave={async patch => {
                setSaving(conduit.id)
                try { setData(await projectsApi.updateProjectConduit(projectId, conduit.id, patch)) }
                catch (error: any) { showError(error?.message ?? t('common.saveFailed')) }
                finally { setSaving(null) }
              }}
            />
          ))}
        </div>
      </details>
    </div>
  )
}

function ConduitEditor({ conduit, canEdit, saving, onSave }: {
  conduit: ComplianceTopology['conduits'][number]
  canEdit: boolean
  saving: boolean
  onSave: (patch: Partial<ComplianceTopology['conduits'][number]>) => Promise<void>
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(conduit)
  useEffect(() => setDraft(conduit), [conduit])
  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-center justify-between gap-2">
        <input value={draft.label} readOnly={!canEdit} onChange={event => setDraft(value => ({ ...value, label: event.target.value }))} className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none dark:text-slate-100" />
        <span className="text-[10px] text-slate-400">{draft.from_zone} → {draft.to_untrusted ? t('zones.untrusted') : draft.to_zone}</span>
      </div>
      {(['purpose', 'boundary_device', 'boundary_control', 'permitted_traffic'] as const).map(field => (
        <label key={field} className="mt-2 block text-[10px] font-medium uppercase text-slate-400">
          {t(`zones.fields.${field}`)}
          <textarea value={draft[field] || 'TBU'} readOnly={!canEdit} onChange={event => setDraft(value => ({ ...value, [field]: event.target.value }))} rows={2} className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs font-normal normal-case text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200" />
        </label>
      ))}
      {canEdit && (
        <Button size="sm" className="mt-2 h-7 px-2 text-xs" disabled={saving} onClick={() => void onSave({ label: draft.label, purpose: draft.purpose, boundary_device: draft.boundary_device, boundary_control: draft.boundary_control, permitted_traffic: draft.permitted_traffic })}>
          <Save className="mr-1 h-3.5 w-3.5" /> {t('common.save')}
        </Button>
      )}
    </div>
  )
}
